"use client";

import { useState } from "react";
import {
  Search,
  FolderPlus,
  Edit,
  Trash2,
  ChevronRight,
  ChevronDown,
  Folder,
} from "lucide-react";
import {CircularProgress} from '@mui/material'
import Card from "../components/Card";
import Button from "../components/Button";
import Modal from "../components/Modal";
import { useCategories } from "../../hooks/useCategories";

const Categories = () => {
  const {
    categories,
    loading,
    createCategory,
    updateCategory,
    deleteCategory,
    createPostFormTemplate,
    getPostFormTemplate,
    updatePostFormTemplate,
    deletePostFormTemplate,
  } = useCategories();

  const [searchTerm, setSearchTerm] = useState("");
  const [expandedCategories, setExpandedCategories] = useState({});
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isPostDetailsModalOpen, setIsPostDetailsModalOpen] = useState(false);
  const [postDetailsCategory, setPostDetailsCategory] = useState(null);
  const [formFields, setFormFields] = useState([]);
  const [isEditingPostFormTemplate, setIsEditingPostFormTemplate] = useState(false);
  const [postDetailIsLoading, setPostDetailIsLoading] = useState(false);

  // Form state for category editing/adding
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    parent_category_id: "",
    priority: 0,
  });

  // Filter categories by search term
  const filteredCategories = categories.filter(
    (category) =>
      category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (category.description || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
  );

  // Get main categories (no parent)
  const mainCategories = filteredCategories.filter(
    (category) => !category.parent_category_id
  );

  // Get subcategories for a parent category
  const getSubcategories = (parentId) => {
    return filteredCategories.filter(
      (category) => category.parent_category_id === parentId
    );
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // Search is already applied via filteredCategories
  };

  const toggleExpand = (categoryId) => {
    setExpandedCategories({
      ...expandedCategories,
      [categoryId]: !expandedCategories[categoryId],
    });
  };

  const handleAddCategory = () => {
    setFormData({
      name: "",
      description: "",
      price: "",
      parent_category_id: "",
      priority: 0,
    });
    setIsAddModalOpen(true);
  };

  const handleEditCategory = (category, e) => {
    e.stopPropagation();
    setSelectedCategory(category);
    setFormData({
      name: category.name,
      description: category.description,
      price: category.price || "",
      parent_category_id: category.parent_category_id || "",
      priority: typeof category.priority === "number" ? category.priority : 0,
    });
    setIsEditModalOpen(true);
  };

  const handleDeleteCategory = (category, e) => {
    e.stopPropagation();
    setSelectedCategory(category);
    setIsDeleteModalOpen(true);
  };

  const handlePostDetails = async (category) => {
    setPostDetailIsLoading(true);
    setPostDetailsCategory(category);
    setIsPostDetailsModalOpen(true);
    setFormFields([]); // Show loading state
    try {
      const detail = await getPostFormTemplate(category.id);
      if (detail && (detail.form_fields?.length > 0 || detail.fields?.length > 0)) {
        setFormFields(detail.form_fields || detail.fields || []);
        setIsEditingPostFormTemplate(true);
      } else {
        setFormFields([]);
        setIsEditingPostFormTemplate(false);
      }
    } catch (err) {
      setFormFields([]);
      setIsEditingPostFormTemplate(false);
    }
    setPostDetailIsLoading(false);
  };

  const handleAddField = (type) => {
    let newField;
    switch (type) {
      case "text":
        newField = {
          type: "text",
          label: "",
          placeholder: "",
          required: false,
          validation: {},
        };
        break;
      case "number":
        newField = {
          type: "number",
          label: "",
          placeholder: "",
          required: false,
          validation: {},
        };
        break;
      case "dropdown":
        newField = {
          type: "dropdown",
          label: "",
          required: false,
          options: [{ value: "", label: "" }],
          validation: {},
        };
        break;
      case "checkbox_group":
        newField = {
          type: "checkbox_group",
          label: "",
          required: false,
          groups: [{ label: "", options: [{ value: "", label: "" }] }],
          validation: {},
        };
        break;
      case "radio":
        newField = {
          type: "radio",
          label: "",
          required: false,
          options: [{ value: "", label: "" }],
          validation: {},
        };
        break;
      default:
        return;
    }
    setFormFields([...formFields, newField]);
  };

  const handleFieldChange = (idx, key, value) => {
    const updated = [...formFields];
    updated[idx][key] = value;
    setFormFields(updated);
  };

  // For dropdown/checkbox_group options/groups
  const handleOptionChange = (fieldIdx, optIdx, key, value) => {
    const updated = [...formFields];
    updated[fieldIdx].options[optIdx][key] = value;
    setFormFields(updated);
  };
  const handleAddOption = (fieldIdx) => {
    const updated = [...formFields];
    updated[fieldIdx].options.push({ value: "", label: "" });
    setFormFields(updated);
  };
  const handleRemoveOption = (fieldIdx, optIdx) => {
    const updated = [...formFields];
    updated[fieldIdx].options.splice(optIdx, 1);
    setFormFields(updated);
  };
  // For checkbox_group groups
  const handleGroupChange = (fieldIdx, groupIdx, key, value) => {
    const updated = [...formFields];
    updated[fieldIdx].groups[groupIdx][key] = value;
    setFormFields(updated);
  };
  const handleAddGroup = (fieldIdx) => {
    const updated = [...formFields];
    updated[fieldIdx].groups.push({
      label: "",
      options: [{ value: "", label: "" }],
    });
    setFormFields(updated);
  };
  const handleRemoveGroup = (fieldIdx, groupIdx) => {
    const updated = [...formFields];
    updated[fieldIdx].groups.splice(groupIdx, 1);
    setFormFields(updated);
  };
  const handleGroupOptionChange = (fieldIdx, groupIdx, optIdx, key, value) => {
    const updated = [...formFields];
    updated[fieldIdx].groups[groupIdx].options[optIdx][key] = value;
    setFormFields(updated);
  };
  const handleAddGroupOption = (fieldIdx, groupIdx) => {
    const updated = [...formFields];
    updated[fieldIdx].groups[groupIdx].options.push({ value: "", label: "" });
    setFormFields(updated);
  };
  const handleRemoveGroupOption = (fieldIdx, groupIdx, optIdx) => {
    const updated = [...formFields];
    updated[fieldIdx].groups[groupIdx].options.splice(optIdx, 1);
    setFormFields(updated);
  };

  const handleRemoveField = (idx) => {
    const updated = [...formFields];
    updated.splice(idx, 1);
    setFormFields(updated);
  };

  // Update handleSaveCategory
  const handleSaveCategory = async (e) => {
    e.preventDefault();
    if (isAddModalOpen) {
      await createCategory({
        name: formData.name,
        description: formData.description,
        price: formData.price ? Number.parseInt(formData.price) : null,
        parent_category_id: formData.parent_category_id
          ? Number.parseInt(formData.parent_category_id)
          : null,
        priority: typeof formData.priority === "number" ? formData.priority : Number.parseInt(formData.priority) || 0,
      });
      setIsAddModalOpen(false);
      console.log(`Category "${formData.name}" has been created`);
    } else {
      await updateCategory(selectedCategory.id, {
        name: formData.name,
        description: formData.description,
        price: formData.price ? Number.parseInt(formData.price) : null,
        parent_category_id: formData.parent_category_id
          ? Number.parseInt(formData.parent_category_id)
          : null,
        priority: typeof formData.priority === "number" ? formData.priority : Number.parseInt(formData.priority) || 0,
      });
      setIsEditModalOpen(false);
      console.log(`Category "${formData.name}" has been updated`);
    }
  };

  const handleConfirmDelete = async () => {
    // Check if category has subcategories
    const hasSubcategories = categories.some(
      (cat) => cat.parent_category_id === selectedCategory.id
    );

    if (hasSubcategories) {
      console.log(
        `Cannot delete category "${selectedCategory.name}" because it has subcategories`
      );
      setIsDeleteModalOpen(false);
      return;
    }

    await deleteCategory(selectedCategory.id);
    setIsDeleteModalOpen(false);
    console.log(`Category "${selectedCategory.name}" has been deleted`);
  };

  const handleSavePostDetails = async () => {
    if (!postDetailsCategory) return;
    try {
      if (isEditingPostFormTemplate) {
        // Update existing post detail
        await updatePostFormTemplate(postDetailsCategory.id, {
          form_fields: formFields,
        });
      } else {
        // Create new post detail
        await createPostFormTemplate(postDetailsCategory.id, {
          form_fields: formFields,
        });
      }
    } catch (err) {
      // Optionally handle error (show notification, etc)
      console.error("Failed to save post details:", err);
    }
    setIsPostDetailsModalOpen(false);
    setPostDetailsCategory(null);
    setFormFields([]);
    setIsEditingPostFormTemplate(false);
  };

  const handleDeletePostDetails = async () => {
    if (!postDetailsCategory) return;
    try {
      await deletePostFormTemplate(postDetailsCategory.id);
    } catch (err) {
      console.error("Failed to delete post details:", err);
    }
    setIsPostDetailsModalOpen(false);
    setPostDetailsCategory(null);
    setFormFields([]);
  };

  const CategoryItem = ({ category, level = 0 }) => {
    const subcategories = getSubcategories(category.id);
    const hasSubcategories = subcategories.length > 0;
    const isExpanded = expandedCategories[category.id];

    return (
      <>
        <div
          className={`flex items-center py-3 px-4 hover:bg-gray-50 cursor-pointer ${
            level > 0 ? "pl-" + (level * 8 + 4) + "px" : ""
          }`}
          onClick={() => hasSubcategories && toggleExpand(category.id)}
        >
          <div className="flex-1 flex items-center min-w-0">
            {hasSubcategories ? (
              isExpanded ? (
                <ChevronDown className="h-5 w-5 text-gray-400 mr-2 flex-shrink-0" />
              ) : (
                <ChevronRight className="h-5 w-5 text-gray-400 mr-2 flex-shrink-0" />
              )
            ) : (
              <div className="w-5 mr-2" />
            )}
            <Folder className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0" />
            <div className="truncate">
              <span className="font-medium text-gray-900">{category.name}</span>
              {typeof category.price !== "undefined" &&
                category.price !== null && (
                  <span className="ml-2 text-sm text-green-600">
                    ${category.price}
                  </span>
                )}
              <span className="ml-2 text-sm text-gray-500">
                {category.has_post ? "(Has Post)" : ""}
              </span>
            </div>
          </div>
          <div className="ml-4 flex-shrink-0 flex items-center">
            <div className="flex space-x-2">
              <Button
                variant="outline"
                size="sm"
                icon={Edit}
                onClick={(e) => handleEditCategory(category, e)}
              />
              <Button
                variant="outline"
                size="sm"
                icon={Trash2}
                onClick={(e) => handleDeleteCategory(category, e)}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePostDetails(category)}
              >
                Post Details
              </Button>
            </div>
          </div>
        </div>
        {isExpanded &&
          subcategories.map((subcat) => (
            <CategoryItem key={subcat.id} category={subcat} level={level + 1} />
          ))}
      </>
    );
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Categories</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage content categories and subcategories
        </p>
      </div>

      <Card>
        <div className="flex flex-col sm:flex-row justify-between mb-4">
          <form onSubmit={handleSearch} className="flex mb-4 sm:mb-0">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search categories..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Button type="submit" variant="outline" className="ml-2">
              Search
            </Button>
          </form>
          <Button icon={FolderPlus} onClick={handleAddCategory}>
            Add Category
          </Button>
        </div>

        {loading ? (
          <div className="animate-pulse space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-200 rounded"></div>
            ))}
          </div>
        ) : mainCategories.length === 0 ? (
          <div className="text-center py-12">
            <Folder className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              No categories found
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by creating a new category.
            </p>
            <div className="mt-6">
              <Button icon={FolderPlus} onClick={handleAddCategory}>
                Add Category
              </Button>
            </div>
          </div>
        ) : (
          <div className="border border-gray-200 rounded-md overflow-hidden">
            <div className="bg-gray-50 py-3 px-4 flex items-center border-b border-gray-200">
              <div className="flex-1 font-medium text-gray-700">Name</div>
              <div className="flex-shrink-0 font-medium text-gray-700">
                Actions
              </div>
            </div>
            <div className="divide-y divide-gray-200">
              {mainCategories.map((category) => (
                <CategoryItem key={category.id} category={category} />
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Add/Edit Category Modal */}
      <Modal
        isOpen={isAddModalOpen || isEditModalOpen}
        onClose={() =>
          isAddModalOpen ? setIsAddModalOpen(false) : setIsEditModalOpen(false)
        }
        title={isAddModalOpen ? "Add Category" : "Edit Category"}
        size="md"
        footer={
          <div className="flex gap-2 justify-end">
            <Button
              variant="outline"
              onClick={() =>
                isAddModalOpen
                  ? setIsAddModalOpen(false)
                  : setIsEditModalOpen(false)
              }
            >
              Cancel
            </Button>
            <Button onClick={handleSaveCategory}>
              {isAddModalOpen ? "Create" : "Save Changes"}
            </Button>
          </div>
        }
      >
        <form onSubmit={handleSaveCategory}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name
              </label>
              <input
                type="text"
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                rows={3}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Price
              </label>
              <input
                type="number"
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={formData.price}
                onChange={(e) =>
                  setFormData({ ...formData, price: e.target.value })
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Parent Category
              </label>
              <select
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={formData.parent_category_id}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    parent_category_id: e.target.value,
                  })
                }
              >
                <option value="">None (Top Level)</option>
                {categories
                  .filter((cat) =>
                    // Only exclude self when editing, not when adding
                    isEditModalOpen
                      ? !selectedCategory || cat.id !== selectedCategory.id
                      : true
                  )
                  .map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority
              </label>
              <input
                type="number"
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={formData.priority}
                onChange={(e) =>
                  setFormData({ ...formData, priority: e.target.value })
                }
                min={0}
              />
            </div>
          </div>
        </form>
      </Modal>

      {/* Delete Category Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete Category"
        size="sm"
        footer={
          <div className="flex gap-2 justify-end">
            <Button
              variant="outline"
              onClick={() => setIsDeleteModalOpen(false)}
            >
              Cancel
            </Button>
            <Button variant="danger" onClick={handleConfirmDelete}>
              Delete
            </Button>
          </div>
        }
      >
        {selectedCategory && (
          <div>
            <p className="text-sm text-gray-500">
              Are you sure you want to delete the category "
              {selectedCategory.name}"? This action cannot be undone.
            </p>
            {categories.some(
              (cat) => cat.parent_category_id === selectedCategory.id
            ) && (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <p className="text-sm text-yellow-700">
                  <strong>Warning:</strong> This category has subcategories. You
                  must delete or reassign all subcategories before deleting this
                  category.
                </p>
              </div>
            )}
            {selectedCategory.post_detail && (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <p className="text-sm text-yellow-700">
                  <strong>Note:</strong> This category has a post detail
                  associated with it.
                </p>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Add Post Details Modal */}
      <Modal
        isOpen={isPostDetailsModalOpen}
        onClose={() => setIsPostDetailsModalOpen(false)}
        title={`Post Details for ${postDetailsCategory?.name || "Category"}`}
        size="lg"
          footer={
            <div className="flex gap-2 justify-end">
          <Button
            variant="outline"
            onClick={() => setIsPostDetailsModalOpen(false)}
          >
            Cancel
          </Button>
          {formFields.length > 0 && (
            <Button variant="danger" onClick={handleDeletePostDetails}>
              Delete Post Details
            </Button>
          )}
          <Button onClick={handleSavePostDetails}>Save Post Details</Button>
            </div>
          }
        >
          {postDetailIsLoading ? (
            <div className="flex justify-center items-center h-full">
              <CircularProgress />
            </div>
          ) : isPostDetailsModalOpen && formFields.length != 0 ? (
          // Render existing form fields and allow for addition, edit or removal
          <div className="space-y-4">
            <div className="mb-4 flex gap-2">
              <Button variant="outline" onClick={() => handleAddField("text")}>
                Add Text Field
              </Button>
              <Button
                variant="outline"
                onClick={() => handleAddField("number")}
              >
                Add Number Field
              </Button>
              <Button
                variant="outline"
                onClick={() => handleAddField("dropdown")}
              >
                Add Dropdown Field
              </Button>
              <Button
                variant="outline"
                onClick={() => handleAddField("checkbox_group")}
              >
                Add Checkbox Group
              </Button>
              <Button variant="outline" onClick={() => handleAddField("radio")}>
                Add Radio Field
              </Button>
            </div>
            {formFields.map((field, idx) => (
              <div key={idx} className="border p-3 rounded-md bg-gray-50">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-semibold">
                    {field.type.replace("_", " ").toUpperCase()}
                  </span>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleRemoveField(idx)}
                  >
                    Remove
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <input
                    type="text"
                    className="border px-2 py-1 rounded"
                    placeholder="Label"
                    value={field.label}
                    onChange={(e) =>
                      handleFieldChange(idx, "label", e.target.value)
                    }
                  />
                  {field.type !== "checkbox_group" &&
                    field.type !== "radio" && (
                      <input
                        type="text"
                        className="border px-2 py-1 rounded"
                        placeholder="Placeholder"
                        value={field.placeholder || ""}
                        onChange={(e) =>
                          handleFieldChange(idx, "placeholder", e.target.value)
                        }
                      />
                    )}
                  {field.type === "ratio" && (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        className="border px-2 py-1 rounded w-28"
                        placeholder="Numerator Placeholder"
                        value={field.numeratorPlaceholder || ""}
                        onChange={(e) =>
                          handleFieldChange(
                            idx,
                            "numeratorPlaceholder",
                            e.target.value
                          )
                        }
                      />
                      <span className="self-center">/</span>
                      <input
                        type="text"
                        className="border px-2 py-1 rounded w-28"
                        placeholder="Denominator Placeholder"
                        value={field.denominatorPlaceholder || ""}
                        onChange={(e) =>
                          handleFieldChange(
                            idx,
                            "denominatorPlaceholder",
                            e.target.value
                          )
                        }
                      />
                    </div>
                  )}
                  <label className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={field.required}
                      onChange={(e) =>
                        handleFieldChange(idx, "required", e.target.checked)
                      }
                    />
                    Required
                  </label>
                </div>
                {/* Field-specific options */}
                {field.type === "dropdown" && (
                  <div className="mt-2">
                    <span className="font-medium">Dropdown Options:</span>
                    {field.options.map((opt, optIdx) => (
                      <div key={optIdx} className="flex gap-2 mt-1">
                        <input
                          type="text"
                          className="border px-2 py-1 rounded"
                          placeholder="Value"
                          value={opt.value}
                          onChange={(e) =>
                            handleOptionChange(
                              idx,
                              optIdx,
                              "value",
                              e.target.value
                            )
                          }
                        />
                        <input
                          type="text"
                          className="border px-2 py-1 rounded"
                          placeholder="Label"
                          value={opt.label}
                          onChange={(e) =>
                            handleOptionChange(
                              idx,
                              optIdx,
                              "label",
                              e.target.value
                            )
                          }
                        />
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleRemoveOption(idx, optIdx)}
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-1"
                      onClick={() => handleAddOption(idx)}
                    >
                      Add Option
                    </Button>
                  </div>
                )}
                {field.type === "radio" && (
                  <div className="mt-2">
                    <span className="font-medium">Radio Options:</span>
                    {field.options.map((opt, optIdx) => (
                      <div key={optIdx} className="flex gap-2 mt-1">
                        <input
                          type="text"
                          className="border px-2 py-1 rounded"
                          placeholder="Value"
                          value={opt.value}
                          onChange={(e) =>
                            handleOptionChange(
                              idx,
                              optIdx,
                              "value",
                              e.target.value
                            )
                          }
                        />
                        <input
                          type="text"
                          className="border px-2 py-1 rounded"
                          placeholder="Label"
                          value={opt.label}
                          onChange={(e) =>
                            handleOptionChange(
                              idx,
                              optIdx,
                              "label",
                              e.target.value
                            )
                          }
                        />
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleRemoveOption(idx, optIdx)}
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-1"
                      onClick={() => handleAddOption(idx)}
                    >
                      Add Option
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <>
            <div className="text-center py-8 text-gray-500">
              No post details found for this category.
            </div>
            <div className="mb-4 flex gap-2 justify-center">
              <Button variant="outline" onClick={() => handleAddField("text")}>
                Add Text Field
              </Button>
              <Button
                variant="outline"
                onClick={() => handleAddField("number")}
              >
                Add Number Field
              </Button>
              <Button
                variant="outline"
                onClick={() => handleAddField("dropdown")}
              >
                Add Dropdown Field
              </Button>
              <Button
                variant="outline"
                onClick={() => handleAddField("checkbox_group")}
              >
                Add Checkbox Group
              </Button>
              <Button variant="outline" onClick={() => handleAddField("radio")}>
                Add Radio Field
              </Button>
            </div>
          </>
          
        )}
      </Modal>
    </div>
  );
};

export default Categories;
